/**
 * Hook for managing user form state and validation
 *
 * Extracted from useUserManagement.js to reduce complexity.
 * Handles username, password, email, validation, and form state.
 */
import { useState, useCallback, useEffect } from 'react';

import {
  USER_MODES,
  // Aliased: this hook defines its own getDefaultRoles/getOptionalRoles that
  // compose these with the admin roles when the user is an account owner.
  getDefaultRoles as getBaseDefaultRoles,
  getOptionalRoles as getBaseOptionalRoles,
  getAdminRoles,
  validateUserForm,
} from '../util/user';
import { getIsAccountOwner } from '../util/storage';

/**
 * Custom hook for user form state and validation
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.existingUsers - Existing users for duplicate checking
 * @param {Object} options.authenticatedUser - Currently authenticated user
 * @param {boolean} options.usersLoaded - Whether users have been loaded
 * @returns {Object} Form state and handlers
 */
export default function useUserForm({ existingUsers = {}, authenticatedUser = null, usersLoaded = false } = {}) {
  // Mode state
  const [mode, setMode] = useState(USER_MODES.create);

  // Form input state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');

  // Validation state
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);

  // Role dropdown state
  const [rolesOpen, setRolesOpen] = useState(false);
  const [rolesFilterValue, setRolesFilterValue] = useState('Reporter');
  const [rolesFilter, setRolesFilter] = useState(getBaseDefaultRoles());

  // Account owner status (affects available roles)
  const [isAccountOwner, setIsAccountOwner] = useState(false);

  // Loading state
  const [updatingUser, setUpdatingUser] = useState(false);

  // Check if current user is an account owner (can assign admin roles)
  useEffect(() => {
    getIsAccountOwner().then(setIsAccountOwner);
  }, []);

  // Build roles list based on account owner status
  const getDefaultRoles = useCallback(() => {
    return isAccountOwner ? [...getBaseDefaultRoles(), ...getAdminRoles()] : getBaseDefaultRoles();
  }, [isAccountOwner]);

  const getOptionalRoles = useCallback(() => {
    return isAccountOwner ? [...getBaseOptionalRoles(), ...getAdminRoles()] : getBaseOptionalRoles();
  }, [isAccountOwner]);

  // Update roles filter when account owner status changes
  useEffect(() => {
    if (mode === USER_MODES.create) {
      setRolesFilter(getDefaultRoles());
    } else {
      setRolesFilter(getOptionalRoles());
    }
  }, [isAccountOwner, mode, getDefaultRoles, getOptionalRoles]);

  /**
   * Validate form using extracted validation utility
   */
  const validateForm = useCallback(() => {
    const { errors: formErrors, isValid } = validateUserForm(
      { email: username, password },
      { mode, existingUsers, authenticatedUser }
    );

    setErrors(formErrors);
    setIsFormValid(isValid);
  }, [username, password, mode, existingUsers, authenticatedUser]);

  /**
   * Clear form input fields
   */
  const clearInput = useCallback(() => {
    setUsername('');
    setPassword('');
    setDisplayErrors(false);
  }, []);

  /**
   * Handle email input (sets both username and email)
   */
  const enterEmail = useCallback((text) => {
    const lowercaseText = text.toLowerCase();
    setUsername(lowercaseText);
    setEmail(lowercaseText);
  }, []);

  /**
   * Switch between create and edit modes
   */
  const switchMode = useCallback((newMode) => {
    setDisplayErrors(false);
    setMode(newMode);
    setUpdatingUser(false);

    if (newMode === USER_MODES.edit) {
      setRolesFilter(getOptionalRoles());
      setRolesFilterValue(null);
    } else {
      setRolesFilter(getDefaultRoles());
      setRolesFilterValue('Reporter');
    }
  }, [getDefaultRoles, getOptionalRoles]);

  // Validate form on input changes or when existing users load
  useEffect(() => {
    validateForm();
  }, [validateForm, existingUsers]);

  // Sync submit enabled with form validity
  useEffect(() => {
    const canSubmit = mode === USER_MODES.create
      ? isFormValid && usersLoaded
      : isFormValid;
    setSubmitEnabled(canSubmit);
  }, [isFormValid, usersLoaded, mode]);

  return {
    // Mode
    mode,
    setMode,
    switchMode,

    // Form inputs
    username,
    setUsername,
    password,
    setPassword,
    email,
    setEmail,

    // Validation
    errors,
    isFormValid,
    displayErrors,
    setDisplayErrors,
    submitEnabled,

    // Role dropdown
    rolesOpen,
    setRolesOpen,
    rolesFilterValue,
    setRolesFilterValue,
    rolesFilter,
    setRolesFilter,

    // Loading
    updatingUser,
    setUpdatingUser,

    // Actions
    validateForm,
    clearInput,
    enterEmail,
  };
}

// Re-export mode constants for convenience
export { USER_MODES };
